import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

import { Link, useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { selectUserInfo } from "../../user/UserSlice";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { selectCartItems } from "../../cart/CartSlice";
import { selectLoggedInUser } from "../../auth/AuthSlice";
import TuneIcon from "@mui/icons-material/Tune";
import MenuIcon from "@mui/icons-material/Menu";
import {
  selectProductIsFilterOpen,
  toggleFilters,
} from "../../products/ProductSlice";
import { SearchBar } from "../../../components/SearchBar";
import logoNew from "../../../assets/images/logonew.jpeg";

export const Navbar = ({ isProductList = false }) => {
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const userInfo = useSelector(selectUserInfo);
  const cartItems = useSelector(selectCartItems);
  const loggedInUser = useSelector(selectLoggedInUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const is480 = useMediaQuery(theme.breakpoints.down(480));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isProductFilterOpen = useSelector(selectProductIsFilterOpen);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleToggleFilters = () => {
    dispatch(toggleFilters());
  };

  const isAdmin = Boolean(loggedInUser?.isAdmin);
  const displayName = userInfo?.name || "Guest";

  const settings = [
    { name: "Home", to: "/" },
    {
      name: "Profile",
      to: isAdmin ? "/admin/profile" : "/profile",
    },
    {
      name: isAdmin ? "Orders" : "My orders",
      to: isAdmin ? "/admin/orders" : "/orders",
    },
    { name: "Logout", to: "/logout" },
  ];

  const mobileLinks = [
    { name: "Home", to: "/" },
    { name: "Offers", to: "/discounts" },
    { name: "Testers", to: "/testers" },
    { name: "Reviews", to: "/reviews-gallery" },
    ...(loggedInUser?._id
      ? [
          { name: isAdmin ? "Admin Panel" : "Profile", to: isAdmin ? "/admin/panel" : "/profile" },
          { name: isAdmin ? "Orders" : "My orders", to: isAdmin ? "/admin/orders" : "/orders" },
          { name: "Logout", to: "/logout" },
        ]
      : [
          { name: "Login", to: "/login" },
          { name: "Signup", to: "/signup" },
        ]),
  ];

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "white",
        boxShadow: "none",
        color: "text.primary",
      }}
    >
      <Toolbar
        sx={{
          p: 1,
          height: { xs: "auto", md: "4rem" },
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: 'wrap',
          minHeight: { xs: "auto", md: "4rem" },
        }}
      >
        {isMobile && (
          <Stack width="100%" rowGap={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Stack
                component={Link}
                to="/"
                direction="row"
                alignItems="center"
                sx={{
                  color: "inherit",
                  textDecoration: "none",
                  width: 56,
                  height: 42,
                  minWidth: 56,
                  position: "relative",
                  overflow: "visible",
                }}
              >
                <Box
                  component="img"
                  src={logoNew}
                  alt="Nexocart Store"
                  sx={{
                    width: 126,
                    height: 126,
                    borderRadius: 1,
                    objectFit: "contain",
                    position: "absolute",
                    left: -31,
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "block",
                  }}
                />
              </Stack>

              <Stack direction="row" alignItems="center" gap={0.5}>
                {cartItems?.length > 0 && (
                  <Badge badgeContent={cartItems.length} color="error">
                    <IconButton onClick={() => navigate("/cart")} size="small">
                      <ShoppingCartOutlinedIcon />
                    </IconButton>
                  </Badge>
                )}
                {isProductList && (
                  <IconButton onClick={handleToggleFilters} size="small">
                    <TuneIcon sx={{ color: isProductFilterOpen ? "black" : "" }} />
                  </IconButton>
                )}
                <IconButton onClick={() => setMobileMenuOpen(true)} size="small" aria-label="Open menu">
                  <MenuIcon />
                </IconButton>
              </Stack>
            </Stack>

            <Box sx={{ width: "100%" }}>
              <SearchBar />
            </Box>

            <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
              <Stack sx={{ width: 280, p: 2 }} rowGap={1}>
                <Stack direction="row" alignItems="center" gap={1} sx={{ pb: 1 }}>
                  <Avatar alt={userInfo?.name} src="null" />
                  <Stack minWidth={0}>
                    <Typography fontWeight={800}>{displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">{isAdmin ? "Admin" : loggedInUser?._id ? "Customer" : "Guest"}</Typography>
                  </Stack>
                </Stack>
                <List disablePadding>
                  {mobileLinks.map((link) => (
                    <ListItemButton key={link.name} component={Link} to={link.to} onClick={() => setMobileMenuOpen(false)} sx={{ borderRadius: 1 }}>
                      <ListItemText primary={link.name} />
                    </ListItemButton>
                  ))}
                </List>
              </Stack>
            </Drawer>
          </Stack>
        )}

        <Stack
          component="a"
          href="/"
          direction="row"
          alignItems="center"
          sx={{
            mr: 2,
            display: { xs: "none", md: "flex" },
            color: "inherit",
            textDecoration: "none",
            width: 78,
            height: 48,
            position: "relative",
            overflow: "visible",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={logoNew}
            alt="Nexocart Store"
            sx={{
              width: 168,
              height: 168,
              borderRadius: 1,
              objectFit: "contain",
              position: "absolute",
              left: -44,
              top: "50%",
              transform: "translateY(-50%)",
              display: "block",
            }}
          />
          
        </Stack>

        {/* Search Bar */}
        <Box sx={{ display: { xs: "none", md: "block" }, flex: 1 }}>
          <SearchBar />
        </Box>

        <Stack
          flexDirection={"row"}
          alignItems={"center"}
          justifyContent={"center"}
          columnGap={2}
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          <Tooltip title="Open settings">
            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
              <Avatar alt={userInfo?.name} src="null" />
            </IconButton>
          </Tooltip>
          <Menu
            sx={{ mt: "45px" }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {isAdmin && (
              <>
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography
                  component={Link}
                  color={"text.primary"}
                  sx={{ textDecoration: "none" }}
                  to="/admin/panel"
                  textAlign="center"
                >
                  🛡️ Admin Control Panel
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography
                  component={Link}
                  color={"text.primary"}
                  sx={{ textDecoration: "none" }}
                  to="/admin/add-product"
                  textAlign="center"
                >
                  ➕ Add new Product
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography
                  component={Link}
                  color={"text.primary"}
                  sx={{ textDecoration: "none" }}
                  to="/admin/add-discount"
                  textAlign="center"
                >
                  🏷️ Create Discount
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography
                  component={Link}
                  color={"text.primary"}
                  sx={{ textDecoration: "none" }}
                  to="/admin/add-tester"
                  textAlign="center"
                >
                  🧪 Add Tester
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography
                  component={Link}
                  color={"text.primary"}
                  sx={{ textDecoration: "none" }}
                  to="/admin/reviews"
                  textAlign="center"
                >
                  ⭐ Review Management
                </Typography>
              </MenuItem>
              <MenuItem onClick={handleCloseUserMenu}>
                <Typography
                  component={Link}
                  color={"text.primary"}
                  sx={{ textDecoration: "none" }}
                  to="/admin/payment-settings"
                  textAlign="center"
                >
                  💳 Payment Settings
                </Typography>
              </MenuItem>
              </>
            )}
              {settings.map((setting) => (
                <MenuItem key={setting.name} onClick={handleCloseUserMenu}>
                  <Typography
                    component={Link}
                    color={"text.primary"}
                    sx={{ textDecoration: "none" }}
                    to={setting.to}
                    textAlign="center"
                  >
                    {setting.name}
                  </Typography>
                </MenuItem>
              ))}
          </Menu>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'common.black', letterSpacing: 0 }}>
              {is480 ? `${String(displayName).split(" ")[0]}` : `Hey, ${displayName}`}
            </Typography>
            {isAdmin && <Button variant="contained">Admin</Button>}
          <Stack
            sx={{
              flexDirection: "row",
              columnGap: "1rem",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!is480 && (
              <>
                <Button component={Link} to="/discounts" size="small" sx={{ textTransform: 'none' }}>
                  Offers
                </Button>
                <Button component={Link} to="/testers" size="small" sx={{ textTransform: 'none' }}>
                  Testers
                </Button>
                <Button component={Link} to="/reviews-gallery" size="small" sx={{ textTransform: 'none' }}>
                  Reviews
                </Button>
              </>
            )}
            {cartItems?.length > 0 && (
              <Badge badgeContent={cartItems.length} color="error">
                <IconButton onClick={() => navigate("/cart")}>
                  <ShoppingCartOutlinedIcon />
                </IconButton>
              </Badge>
            )}
            {isProductList && (
              <IconButton onClick={handleToggleFilters}>
                <TuneIcon sx={{ color: isProductFilterOpen ? "black" : "" }} />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
